import { useEffect, useRef, useState } from 'react';
import { EasyFlexWorker, fetchEasyflexProfileByRegnum } from '../api/workersApi';

/**
 * Shared read layer.
 *
 * Consumers that used to read `worker.easyflexWorker.*` off the local mirror
 * table now call this hook with the worker's `easyflexRegistrationNumber`
 * and get the same shape back — sourced live from central-db via the
 * optima-backend proxy at `/workers/easyflex/profile/by-regnum/:regnum`.
 *
 * Design notes:
 * - Module-level cache keyed by regnum, 60s TTL. Short enough that stale
 *   sync state doesn't linger long, long enough that switching tabs on the
 *   worker detail page doesn't trigger a refetch.
 * - Module-level in-flight map dedups concurrent requests for the same
 *   regnum — WorkerDetail + EasyFlexWorkerForm + SyncStatusIndicator can
 *   all mount in the same render pass and share one HTTP call.
 * - `null` / `undefined` regnum → no fetch, returns idle state (workers
 *   not linked to EasyFlex just render empty in the consuming components).
 */

interface CacheEntry {
    data: EasyFlexWorker | null;
    fetchedAt: number;
    error: unknown | null;
}

const CACHE_TTL_MS = 60_000;
const cache = new Map<string, CacheEntry>();
const inFlight = new Map<string, Promise<EasyFlexWorker | null>>();

function isFresh(entry: CacheEntry): boolean {
    return Date.now() - entry.fetchedAt < CACHE_TTL_MS;
}

async function fetchDedup(
    key: string,
    regnum: string,
    organization?: string | null,
): Promise<EasyFlexWorker | null> {
    const running = inFlight.get(key);
    if (running) return running;

    const p = (async (): Promise<EasyFlexWorker | null> => {
        try {
            const data = await fetchEasyflexProfileByRegnum(regnum, organization);
            cache.set(key, { data, fetchedAt: Date.now(), error: null });
            return data;
        } catch (err) {
            // Cache the error too so we don't hammer central-db on repeated
            // renders. Consumers can force a retry via `refresh`.
            cache.set(key, { data: null, fetchedAt: Date.now(), error: err });
            throw err;
        } finally {
            inFlight.delete(key);
        }
    })();

    inFlight.set(key, p);
    return p;
}

export interface UnifiedEasyflexProfileState {
    data: EasyFlexWorker | null;
    loading: boolean;
    error: unknown | null;
    refresh: () => void;
}

export function useUnifiedEasyflexProfile(
    regnum: string | number | null | undefined,
    organization?: string | null,
): UnifiedEasyflexProfileState {
    // Cache key includes the organization: the same regnum exists under both
    // orgs for 100+ workers, so an org-less key could serve the wrong org's
    // profile from cache even after the API learned to disambiguate.
    const key = regnum != null ? `${String(regnum)}::${organization ?? ''}` : null;
    const cached = key ? cache.get(key) : undefined;

    const [data, setData] = useState<EasyFlexWorker | null>(
        cached && isFresh(cached) ? cached.data : null,
    );
    const [error, setError] = useState<unknown | null>(
        cached && isFresh(cached) ? cached.error : null,
    );
    const [loading, setLoading] = useState<boolean>(
        !!key && (!cached || !isFresh(cached)),
    );
    // Bumping this ref forces a refetch even for fresh cache entries.
    const revalidateRef = useRef(0);

    useEffect(() => {
        if (!key) {
            setData(null);
            setError(null);
            setLoading(false);
            return;
        }

        const entry = cache.get(key);
        if (entry && isFresh(entry) && revalidateRef.current === 0) {
            setData(entry.data);
            setError(entry.error);
            setLoading(false);
            return;
        }

        let cancelled = false;
        setLoading(true);
        setError(null);
        fetchDedup(key, String(regnum), organization)
            .then((result) => {
                if (cancelled) return;
                setData(result);
                setError(null);
                setLoading(false);
            })
            .catch((err) => {
                if (cancelled) return;
                setData(null);
                setError(err);
                setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [key, revalidateRef.current]);

    const refresh = (): void => {
        if (!key) return;
        cache.delete(key);
        revalidateRef.current += 1;
        // Trigger the effect to re-run.
        setLoading(true);
    };

    return { data, loading, error, refresh };
}

/**
 * Escape hatch — clears the whole module-level cache. Useful after a
 * mutation that we know invalidates every profile (e.g. a bulk sync run
 * finishing). Prefer `refresh` on a specific hook instance when possible.
 */
export function invalidateAllUnifiedEasyflexProfiles(): void {
    cache.clear();
}
